import { addDays, isBefore } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import _ from 'lodash';
import { useEffect, useRef } from 'react';

import type { UseQueryResult } from 'react-query';

declare global {
  interface Window {
    _: unknown;
    LEGACY_GLOBALS: Globals;
    query?: string;
    universalHeaderDesignTokensError?: boolean;
    universalHeaderPrimaryDesignTokensFailed?: boolean;
  }
}

export enum Environment {
  local = 'local',
  dev = 'dev',
  qa = 'qa',
  prod = 'prod',
}

export interface Globals {
  environment?: Environment;
  designTokensEndpoint?: string;
  apiBaseUrl?: string;
  userId?: number;
  refreshCookieStore?: () => void;
}

export function getPort() {
  // In test environments, global.createMockLocation is set up by jest-setup.js.
  // In the browser, fall back to window.location.port.
  if (typeof (global as any).createMockLocation === 'function') {
    return (global as any).createMockLocation().port;
  }
  return window.location.port;
}

export const getLearningManagerHost = (testhost?: string) => {
  // Use the argument if provided, otherwise use window.location.hostname
  const useHost = testhost ?? window.location.hostname;

  if (useHost) {
    if (useHost === 'catalog.bai.org') {
      return 'https://lmgr.bai.org';
    }
    if (useHost.startsWith('catalog-uat') && useHost.endsWith('.bai.org')) {
      return 'https://lmgr-uat2.bai.org';
    }
    if (useHost.startsWith('catalog-qa') && useHost.endsWith('.bai.org')) {
      return 'https://lmgr-qa4.bai.org';
    }
    if (useHost.startsWith('catalog-dev') && useHost.endsWith('.bai.org')) {
      return 'https://lmgr-dev3.bai.org';
    }
    if (useHost === 'localhost' || useHost.endsWith('.local')) {
      return `http://localhost:${getPort()}`;
    }
  }
  return 'https://lmgr.bai.org'; // defaults to prod
};

export const getPublicCatalogHost = (testhost?: string) => {
  const useHost = testhost ?? window.location.hostname;

  if (useHost) {
    if (useHost === 'lmgr.bai.org') {
      return 'https://catalog.bai.org';
    }
    if (useHost.startsWith('lmgr-uat') && useHost.endsWith('.bai.org')) {
      return 'https://catalog-uat2.bai.org';
    }
    if (useHost.startsWith('lmgr-qa') && useHost.endsWith('.bai.org')) {
      return 'https://catalog-qa4.bai.org';
    }
    if (useHost.startsWith('lmgr-dev') && useHost.endsWith('.bai.org')) {
      return 'https://catalog-dev3.bai.org';
    }
    if (useHost === 'catalog.web.local') {
      return `http://catalog.web.local:${getPort()}`;
    }
    if (useHost === 'localhost') {
      return `http://catalog.web.local:${getPort()}`;
    }
  }
  return 'https://catalog.bai.org'; // defaults to prod
};

export const getDesignTokensEndpoint = (
  environment?: Environment,
  userId?: number,
  configuredApiBaseUrl?: string,
): string => {
  const hostname = window.location.hostname.toLowerCase();

  // Extract hostname from configured URL for reliable environment detection
  let configuredHostname = '';
  if (configuredApiBaseUrl) {
    try {
      configuredHostname = new URL(configuredApiBaseUrl.toLowerCase()).hostname;
    } catch {
      configuredHostname = '';
    }
  }

  let apiBaseUrl: string;

  // // On localhost (webpack dev server on :5200 or containerised nginx on :7201),
  //   // route through the proxy to avoid CORS.
  //   if (
  //     typeof window !== 'undefined' &&
  //     window.location.hostname === 'localhost'
  //   ) {
  //     let endpoint = `/erc-api-proxy/api/v1/header/design-tokens?abbreviation=lms`;
  //     if (userId && userId.toString().length >= 4) {
  //       endpoint += `&userId=${userId}`;
  //     }
  //     return endpoint;
  //   }

  //   // On localhost (webpack dev server on :5200 or containerised nginx on :7201),
  //   // route through the proxy to avoid CORS.
  //   if (
  //     typeof window !== 'undefined' &&
  //     window.location.hostname === 'localhost'
  //   ) {
  //     let endpoint = `/erc-api-proxy/api/v1/header/design-tokens?abbreviation=lms`;
  //     if (userId && userId.toString().length >= 4) {
  //       endpoint += `&userId=${userId}`;
  //     }
  //     return endpoint;
  //   }

  if (
    /^.*-uat(?:[^a-z]|$)/i.test(configuredHostname) ||
    /^.*-uat(?:[^a-z]|$)/i.test(hostname)
  ) {
    apiBaseUrl = 'https://erc-api-uat-uscz.apis.ld.az.bai.org';
  } else if (
    /^.*-qa(?:[^a-z]|$)/i.test(configuredHostname) ||
    environment === Environment.qa ||
    /^.*-qa(?:[^a-z]|$)/i.test(hostname)
  ) {
    apiBaseUrl = 'https://erc-api-qa-uscz.apis.ld.az.bai.org';
  } else if (
    /^.*-dev(?:[^a-z]|$)/i.test(configuredHostname) ||
    environment === Environment.dev ||
    /^.*-dev(?:[^a-z]|$)/i.test(hostname)
  ) {
    apiBaseUrl = 'https://erc-api-dev-uscz.apis.ld.az.bai.org';
  } else if (
    environment === Environment.prod ||
    (hostname !== 'localhost' && !hostname.endsWith('.local'))
  ) {
    apiBaseUrl = 'https://erc-api-uscz.apis.ld.az.bai.org';
  } else {
    apiBaseUrl = 'http://localhost:5111';
  }

  let endpoint = `${apiBaseUrl}/api/v1/header/design-tokens?abbreviation=lms`;

  if (userId && userId.toString().length >= 4) {
    endpoint += `&userId=${userId}`;
  }

  return endpoint;
};

export const getErrorMessageFromAxiosException = (ex: {
  response: { data: string | { ErrorMessage: string } };
  message: string;
}): string => {
  if (typeof ex.response?.data === 'string') {
    // true for errors when completing tasks
    return ex.response?.data;
  }
  if (typeof ex.response?.data?.ErrorMessage === 'string') {
    return ex.response.data.ErrorMessage;
  }
  if (typeof ex.message === 'string') {
    return ex.message;
  }
  return 'An unknown error occurred';
};

export const makeNameUrlFriendly = (name: string) =>
  encodeURI(name.toLowerCase().replace(/ /g, '-').replace(/\//g, '-'));

export const getStyleForButtonToLookLikeLink = (linkColor?: string) => ({
  background: 'white',
  border: 'none',
  color: linkColor || '#1eabe3',
  padding: '0',
  textDecoration: 'underline',
});

export const getImageSrc = (imageName?: string) => {
  if (!imageName) {
    imageName = 'catalog/default';
  }

  // regex matches for file extensions that begin with . and have 1-5 characters case insensitive
  const hasFileExtension = !!imageName.match(/^.*\.[0-9a-z]{1,5}$/i);

  return `app/images/${imageName}${hasFileExtension ? '' : '.png'}`;
};

export const getPath = (
  pathname: string,
  destination: string,
  isSearch: boolean,
  isViewResultsLinkAtTopOfPage: boolean,
  isPublic?: boolean,
) => {
  const pathArray: string[] = pathname.split('/');
  const isDocuments = pathArray.indexOf('documents-catalog') >= 0;
  let indexOfCatalogReact;
  if (isPublic) {
    indexOfCatalogReact = pathArray.indexOf('browse');
  } else {
    indexOfCatalogReact = pathArray.indexOf(
      isDocuments ? 'documents-catalog' : 'catalog',
    );
  }

  if (indexOfCatalogReact > 0) {
    if (isViewResultsLinkAtTopOfPage) {
      pathArray.pop();
    }

    let newPath = makeNameUrlFriendly(destination);
    while (pathArray.length > indexOfCatalogReact) {
      const parentPath = pathArray.pop();
      newPath = parentPath ? `${parentPath}/${newPath}` : newPath;
    }

    if (isSearch) {
      if (isPublic) {
        newPath = newPath.replace('browse/', 'search/');
      } else {
        newPath = newPath.replace('catalog/', 'catalog/search/');
      }
    }

    return `/${newPath}`;
  }
  return pathname;
};

export const getDueSoon = (date?: string | null) =>
  date ? isBefore(new Date(date), addDays(new Date(), 7)) : false;

export const getOverdue = (date?: string | null) =>
  date ? isBefore(new Date(date), new Date()) : false;

export const usePreviousString = (currentString: string) => {
  // Note: that when writing unit tests for components that use this, it might be necessary to spy on this hook.  See SearchResults.test, 'resets to page 1 if searchString changes'
  const prevString: React.MutableRefObject<string | undefined> = useRef('');
  useEffect(() => {
    prevString.current = currentString;
  });
  return prevString.current;
};

export const StorageVariables = {
  disableDashboard: 'disableDashboard',
  true: 'true',
};

const ONE_MINUTE = 60 * 1000;
const ONE_HOUR = 60 * ONE_MINUTE;

export const TimesInMilliseconds = {
  ZERO: 0,
  THREE_SECONDS: 3 * 1000,
  TEN_SECONDS: 10 * 1000,
  FIVE_HUNDRED_MS: 500,
  ONE_MINUTE,
  FIVE_MINUTES: 5 * ONE_MINUTE,
  FIFTEEN_MINUTES: 15 * ONE_MINUTE,
  THIRTY_MINUTES: 30 * ONE_MINUTE,
  ONE_HOUR,
  FOUR_HOURS: 4 * ONE_HOUR,
};

export const translateCourseType = (type: string) => {
  switch (type.toLowerCase().replace(/\s+/g, '')) {
    case 'careerpath':
      return 'Career Path';
    case 'coursegroup':
      return 'Course Group';
    case 'coursegroupcurricula':
      return 'Course Group Curricula';
    case 'coursegroupcertification':
      return 'Course Group Certification';
    case 'ilt':
      return 'Instructor Led Training';
    case 'wbt':
    case 'webbasedtraining':
      return 'Web Based Training';
    case 'assessment':
    case 'evaluation':
    case 'survey':
    case 'excel':
    case 'word':
    case 'other':
      return _.capitalize(type);
    case 'mp3':
    case 'mp4':
    case 'pdf':
    case 'url':
      return type.toUpperCase();
    default:
      return _.capitalize(type);
  }
};

export function caseInsensitiveSort(stringArray: string[]) {
  return stringArray.sort((a, b) => {
    const lowerA = a.toLowerCase();
    const lowerB = b.toLowerCase();
    return lowerA.localeCompare(lowerB, 'en-us', { sensitivity: 'base' });
  });
}

export function sortByName<T extends { name: string }[]>(courses: T): T {
  return courses.sort((a, b) => {
    const lowerA = a.name.toLowerCase();
    const lowerB = b.name.toLowerCase();
    return lowerA.localeCompare(lowerB, 'en-US', { sensitivity: 'base' });
  });
}

export function sortBySpecifiedAttribute<
  T extends { [attributeName: string]: string }[],
>(courses: T, attributeName: string): T {
  return courses.sort((a, b) => {
    const lowerA = a[attributeName].toLowerCase();
    const lowerB = b[attributeName].toLowerCase();
    return lowerA.localeCompare(lowerB, 'en-US', { sensitivity: 'base' });
  });
}

export const isDocument = (courseType: string) => {
  switch (courseType.toLowerCase()) {
    case 'excel':
    case 'mp3':
    case 'mp4':
    case 'pdf':
    case 'url':
    case 'word':
    case 'other':
      return true;
    default:
      return false;
  }
};

export const convertHexToRgb = (hexString: string) => {
  // useful to lighten theme colors
  const hex = hexString.replace('#', '');
  try {
    if (hex.length === 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return { r, g, b };
    }
    if (hex.length === 3) {
      const r = parseInt(`${hex.substring(0, 1)}${hex.substring(0, 1)}`, 16);
      const g = parseInt(`${hex.substring(1, 2)}${hex.substring(1, 2)}`, 16);
      const b = parseInt(`${hex.substring(2, 3)}${hex.substring(2, 3)}`, 16);
      return { r, g, b };
    }
    throw Error();
  } catch {
    console.error(
      `Error in convertHexToRgb. Invalid color provided: ${hexString}, returning { r: 80, g: 80, b: 80 }`,
    );
    return { r: 80, g: 80, b: 80 };
  }
};

export const getDisplayContentTypeForAssignment = (type: string) => {
  switch (type.toLowerCase()) {
    case 'careerpath':
      return 'Career Path';
    case 'coursegroup':
      return 'Course Group';
    case 'coursegroupcurricula':
      return 'Curricula';
    case 'coursegroupcertification':
      return 'Certification';
    case 'ilt':
      return 'Instructor Led Training';
    case 'wbt':
      return 'Web Based Training';
    case 'assessment':
    case 'evaluation':
    case 'survey':
    case 'excel':
    case 'word':
    case 'other':
      return _.capitalize(type);
    case 'mp3':
    case 'mp4':
    case 'pdf':
    case 'url':
      return type.toUpperCase();
    default:
      return _.capitalize(type);
  }
};

export const getStartOfDayInPTZ = (date: Date, ptz: string) => {
  const dateInPTZ = utcToZonedTime(date, ptz);
  dateInPTZ.setHours(3, 0, 0, 0); // set to 3:00 AM PTZ
  return zonedTimeToUtc(dateInPTZ, ptz);
};

export const getEndOfDayInPTZ = (date: Date, ptz: string) => {
  const dateInPTZ = utcToZonedTime(date, ptz);
  dateInPTZ.setHours(20, 59, 0, 0); // set to 8:59 PM PTZ
  return zonedTimeToUtc(dateInPTZ, ptz);
};

export const useDataSpyMockReturnValue: UseQueryResult<unknown, unknown> = {
  data: null,
  error: null,
  isError: false,
  isSuccess: true,
  isIdle: false,
  isLoading: false,
  isLoadingError: false,
  isRefetchError: false,
  status: 'success', // 'idle' | 'loading' | 'error' | 'success'
  refetch: () =>
    Promise.resolve({
      data: null,
      error: null,
      isError: false,
      isSuccess: true,
      isIdle: false,
      isLoading: false,
      status: 'success',
      refetch: async () => useDataSpyMockReturnValue,
      remove: async () => useDataSpyMockReturnValue,
      dataUpdatedAt: 0,
      errorUpdatedAt: 0,
      errorUpdateCount: 0,
      failureCount: 0,
      isFetched: true,
      isFetchedAfterMount: true,
      isFetching: false,
      isRefetchError: false,
      isStale: false,
      isLoadingError: false,
      isPlaceholderData: false,
      isPreviousData: false,
      isRefetching: false,
    }),
  remove: () =>
    Promise.resolve({
      data: null,
      error: null,
      isError: false,
      isSuccess: true,
      isIdle: false,
      isLoading: false,
      status: 'success',
      refetch: async () => useDataSpyMockReturnValue,
      remove: async () => useDataSpyMockReturnValue,
      dataUpdatedAt: 0,
      errorUpdatedAt: 0,
      errorUpdateCount: 0,
      failureCount: 0,
      isFetched: true,
      isFetchedAfterMount: true,
      isFetching: false,
      isRefetchError: false,
      isStale: false,
      isLoadingError: false,
      isPlaceholderData: false,
      isPreviousData: false,
      isRefetching: false,
    }),
  dataUpdatedAt: 0,
  errorUpdatedAt: 0,
  errorUpdateCount: 0,
  failureCount: 0,
  isFetched: true,
  isFetchedAfterMount: true,
  isFetching: false,
  isStale: false,
  isPlaceholderData: false,
  isPreviousData: false,
  isRefetching: false,
};
